// The single Supabase client for the whole app. Built from the PUBLIC publishable key
// (safe in client code — access is bounded by row-level security), with env overrides so a
// build can point at a staging project without a code change.
//
// OFFLINE-FIRST: the session is persisted to localStorage and auto-refreshed, so a returning
// user stays signed in with no network. That cached session — plus the tier we cache alongside
// it (see ./store) — is what lets premium tiles unlock offline. The whole app must keep working
// signed-OUT; this module only ever ADDS capability, it never gates the chart.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export const SUPABASE_URL =
  (import.meta.env?.VITE_SUPABASE_URL as string | undefined) ?? 'https://ausolarpeuemimpmbiho.supabase.co';
const SUPABASE_ANON_KEY =
  (import.meta.env?.VITE_SUPABASE_ANON_KEY as string | undefined) ??
  'sb_publishable_CdUNKIy2P6cdyxFdbid4SQ_NcGzmvSg';

// Storage key for the persisted session. Namespaced like the rest of the app's localStorage
// (seabearing:*) so it's obvious what it is and easy to clear.
const AUTH_STORAGE_KEY = 'seabearing:auth';

// A localStorage adapter that degrades to in-memory if storage is unavailable (private mode,
// or storage quota/security errors). The rest of the app already tolerates a throwing
// localStorage; auth must too, or a private-mode browser would fail to construct the client.
const memory = new Map<string, string>();
const safeStorage = {
  getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return memory.get(key) ?? null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      memory.set(key, value);
    }
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      memory.delete(key);
    }
  },
};

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Stay signed in across reloads / restarts with no network round-trip.
    persistSession: true,
    autoRefreshToken: true,
    storage: safeStorage,
    storageKey: AUTH_STORAGE_KEY,
    // Pick up the session from the URL fragment after a magic-link / OAuth redirect.
    detectSessionInUrl: true,
    // PKCE is the recommended browser flow (works for magic-link + OAuth without a secret).
    flowType: 'pkce',
  },
});
